import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, PhoneCall, Calculator, Target } from 'lucide-react';
import { VoiceVisual, PosVisual, RagVisual } from '../components/ProjectVisuals';

const featured = [
  {
    to: '/project-eight',
    icon: GraduationCap,
    visual: 'img:/passpoint/hero.png',
    title: 'PassPoint — Exam-Prep Platform',
    desc: 'A bilingual Q-bank and practice platform for Saudi licensing exams — AI explanations, timed exam modes, gamification, and analytics.',
  },
  {
    to: '/project-six',
    icon: PhoneCall,
    visual: 'voice',
    title: 'Voice AI Appointment System',
    desc: 'An AI voice agent that books, reschedules, and follows up end to end — VAPI, GHL CRM, Supabase, and n8n.',
  },
  {
    to: '/project-five',
    icon: Calculator,
    visual: 'pos',
    title: 'Web App Based POS System',
    desc: 'A point-of-sale that runs in the browser — transactions, real-time inventory, and reporting without legacy hardware.',
  },
  {
    to: '/project-four',
    icon: Target,
    visual: 'rag',
    title: 'Intent Classification with RAG',
    desc: 'Emails classified into structured intents and answered with document-grounded AI — Claude + embeddings over internal docs.',
  },
];


const Projects: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100 relative">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />

      <header className="relative z-20 px-4 sm:px-6 lg:px-8 pt-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center rounded-xl border border-zinc-800 bg-[#0d0d0d]/90 backdrop-blur px-5 py-3">
            <Link to="/" className="flex items-center">
              <img src="/pattrnworks_logo_transparent_small.png" alt="PattrnWorks" className="h-9 brightness-0 invert" />
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Home</Link>
              <Link to="/careers" className="hidden sm:block text-sm text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Careers</Link>
              <Link to="/#contact" className="bg-zinc-100 hover:bg-violet-400 text-zinc-900 text-sm font-medium px-4 py-2 rounded-md transition-colors duration-200 flex items-center">
                Start a project
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <section className="relative z-10 py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <span className="font-mono text-xs tracking-[0.2em] text-violet-400 uppercase">{'// Selected work'}</span>
            <h1 className="mt-4 text-3xl md:text-5xl font-bold text-zinc-100">Case studies</h1>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {featured.map((p) => {
              const Icon = p.icon;
              return (
                <Link key={p.to} to={p.to} className="group">
                  <div className="relative h-full rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-violet-400/40 transition-all duration-300">
                    <div className="relative z-10 p-7 md:p-8">
                      <div className="flex items-start justify-between mb-7">
                        <div className="w-12 h-12 rounded-lg border border-zinc-700 bg-zinc-900 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-violet-400" />
                        </div>
                        <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-violet-400 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                      <div className="rounded-lg border border-zinc-800 bg-zinc-950 mb-6 h-28 overflow-hidden">
                        {p.visual === 'voice' && <VoiceVisual />}
                        {p.visual === 'pos' && <PosVisual />}
                        {p.visual === 'rag' && <RagVisual />}
                        {p.visual && p.visual.startsWith('img:') && <img src={p.visual.slice(4)} alt={p.title} className="w-full h-full object-cover object-top" />}
                      </div>
                      <h3 className="text-xl font-semibold text-zinc-100 mb-3">{p.title}</h3>
                      <p className="text-zinc-400 leading-relaxed text-sm">{p.desc}</p>
                      <span className="mt-6 inline-flex items-center text-sm text-zinc-300 group-hover:text-violet-400 transition-colors">
                        View case study <ArrowRight className="ml-1.5 w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-zinc-800/80 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-zinc-800/80 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-zinc-500 text-sm">© {new Date().getFullYear()} pattrnworks. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Projects;
